import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const CommentsContext = createContext({
    isCommentsBarOpen: false,
    toggleCommentsBar: () => {},
    closeCommentsBar: () => {},
    animateCommentsBar: false
});

export const useCommentsBarContext = () => {
    const context = useContext(CommentsContext);

    if (!context) {
        throw new Error('useCommentsBarContext must be used within a CommentsProvider');
    }

    const isCommentsBarOpen = useMemo(() => context.isCommentsBarOpen, [context.isCommentsBarOpen]);
    const animateCommentsBar = useMemo(() => context.animateCommentsBar, [context.animateCommentsBar]);

    return {
        isCommentsBarOpen,
        animateCommentsBar,
        toggleCommentsBar: context.toggleCommentsBar,
        closeCommentsBar: context.closeCommentsBar
    };
};

export const CommentsProvider = ({ children }) => {
    const { pathname } = useLocation();
    const [isCommentsBarOpen, setIsCommentsBarOpen] = useState(false);
    const [animate, setAnimate] = useState(false);

    const toggleCommentsBar = useCallback(() => {
        setAnimate(true);
        setIsCommentsBarOpen(prev => !prev);
    }, []);

    const closeCommentsBar = useCallback(() => {
        setAnimate(true);
        setIsCommentsBarOpen(false);
    }, []);

    useEffect(() => {
        setIsCommentsBarOpen(false);
        setAnimate(false);
    }, [pathname]);

    return (
        <CommentsContext.Provider
            value={{
                isCommentsBarOpen,
                toggleCommentsBar,
                closeCommentsBar,
                animateCommentsBar: animate
            }}
        >
            {children}
        </CommentsContext.Provider>
    );
};
